import React, { useState, useEffect } from 'react';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Page from '@/components/Page';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Chip from '@/components/Chip';
import Alert from '@/components/Alert';
import { Skill, SkillsAPI } from '@/services/skillsAPI';
import SkillEditModal from './SkillEditModal';

const Skills: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>('');

  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await SkillsAPI.getSkills();
      if (response.success) {
        setSkills(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching skills:', err);
      setError(err.response?.data?.error || 'Failed to fetch skills');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (skill: Skill) => {
    setSelectedSkill(skill);
    setShowEditModal(true);
  };

  const handleEditSuccess = (updatedSkill: Skill) => {
    setSkills((prevSkills) =>
      prevSkills.map((s) => (s.id === updatedSkill.id ? updatedSkill : s)),
    );
    setSuccess('Skill updated successfully');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const filteredSkills = skills.filter((skill) => {
    if (!searchInput) return true;
    const search = searchInput.toLowerCase();
    return (
      skill.name.toLowerCase().includes(search) ||
      skill.display_name_en?.toLowerCase().includes(search) ||
      skill.display_name_es?.toLowerCase().includes(search)
    );
  });

  const getBooleanChip = (value: boolean, label: string) => {
    return value ? (
      <Chip color="success" label={label} size="small" />
    ) : (
      <Chip color="default" label={label} size="small" />
    );
  };

  if (loading && skills.length === 0) {
    return (
      <Page title="Skills Management">
        <Card>
          <LoadingState />
        </Card>
      </Page>
    );
  }

  return (
    <Page size="full" title="Skills Management">
      {error && (
        <Alert
          description={error}
          status="danger"
          title="Error"
          onDismiss={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          description={success}
          status="success"
          title="Success"
          onDismiss={() => setSuccess(null)}
        />
      )}

      <Card>
        <Card.Section>
          <Stack gap={12} vertical>
            <Stack gap={12}>
              <div style={{ flex: 1, maxWidth: '400px' }}>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
            </Stack>
            <Small>
              Showing {filteredSkills.length} of {skills.length} skills
            </Small>
          </Stack>
        </Card.Section>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>
                  Display Name (EN)
                </th>
                <th style={{ padding: '12px', textAlign: 'left' }}>
                  Display Name (ES)
                </th>
                <th style={{ padding: '12px', textAlign: 'center' }}>
                  AI Enabled
                </th>
                <th style={{ padding: '12px', textAlign: 'center' }}>
                  English Only
                </th>
                <th style={{ padding: '12px', textAlign: 'center' }}>
                  Gravy Trained
                </th>
                <th style={{ padding: '12px', textAlign: 'center' }}>
                  Requestable
                </th>
                <th style={{ padding: '12px', textAlign: 'center' }}>
                  Uniform
                </th>
                <th style={{ padding: '12px', textAlign: 'center' }}>
                  Requirements
                </th>
                <th style={{ padding: '12px', textAlign: 'center' }}>
                  Certificate
                </th>
                <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSkills.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    style={{ padding: '24px', textAlign: 'center' }}
                  >
                    <Body>No skills found.</Body>
                  </td>
                </tr>
              ) : (
                filteredSkills.map((skill) => (
                  <tr key={skill.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>
                      <Body weight="medium">{skill.name}</Body>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Small>{skill.display_name_en || '-'}</Small>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Small>{skill.display_name_es || '-'}</Small>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {getBooleanChip(skill.ai_enabled, 'AI')}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {getBooleanChip(skill.english_only, 'EN')}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {getBooleanChip(skill.gravy_trained, 'Trained')}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {getBooleanChip(skill.requestable, 'Req')}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {skill.default_uniform_id ? (
                        <Chip color="success" label="Yes" size="small" />
                      ) : (
                        <Chip color="default" label="None" size="small" />
                      )}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <Small>{skill.requirements?.length || 0}</Small>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <Small>{skill.certificate?.length || 0}</Small>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleEditClick(skill)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 8px',
                        }}
                        title="Edit"
                      >
                        <FontAwesomeIcon
                          icon={faEdit}
                          style={{ fontSize: '16px', color: '#666' }}
                        />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {showEditModal && selectedSkill && (
        <SkillEditModal
          skill={selectedSkill}
          hideModal={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}
    </Page>
  );
};

export default Skills;
